import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Checkbox } from './checkbox';
import { Badge } from './badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import { Calendar } from './calendar';
import { ScrollArea } from './scroll-area';
import {
  Plus,
  Loader2,
  Upload,
  X,
  Calendar as CalendarIcon,
  FileText,
  Check,
  ChevronsUpDown,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Inline Custom Field Editor
 * Allows adding/editing custom field values directly within a form panel
 * 
 * @param {string} entityType - 'patient' or 'specialist'
 * @param {Array} customFieldValues - Current custom field values [{field_id, value}]
 * @param {Function} onValuesChange - Callback when values change
 */
export const CustomFieldInlineEditor = ({
  entityType = 'patient',
  customFieldValues = [],
  onValuesChange
}) => {
  const { t } = useTranslation('common');
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localValues, setLocalValues] = useState({});
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [openFieldSelector, setOpenFieldSelector] = useState(false);
  const [uploading, setUploading] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRefs = useRef({});

  // Fetch available custom fields
  useEffect(() => {
    fetchAvailableFields();
  }, [entityType]);

  // Initialize local values from props
  useEffect(() => {
    const values = {};
    customFieldValues.forEach(cfv => {
      values[cfv.field_id] = cfv.value;
    });
    setLocalValues(values);
  }, [customFieldValues]);

  const fetchAvailableFields = async () => {
    setLoading(true);
    try {
      const endpoint = entityType === 'patient' 
        ? `${API_URL}/api/patient-custom-fields/fields`
        : `${API_URL}/api/specialist-custom-fields/fields`;
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(endpoint, { headers });
      // Filter active fields only
      const activeFields = (response.data || []).filter(f => f.status === 'active');
      setAvailableFields(activeFields);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
    setLoading(false);
  };

  const handleValueChange = (fieldId, value) => {
    const newValues = { ...localValues, [fieldId]: value };
    setLocalValues(newValues);
    
    // Convert to array format for parent - include all fields that have been added
    // (even empty ones, as they represent the user's intent to fill them)
    const valuesArray = Object.entries(newValues)
      .map(([field_id, val]) => ({ field_id, value: val }));
    
    onValuesChange(valuesArray);
  };

  const handleRemoveField = (fieldId) => {
    const newValues = { ...localValues };
    delete newValues[fieldId];
    setLocalValues(newValues);
    
    const valuesArray = Object.entries(newValues)
      .map(([field_id, value]) => ({ field_id, value }));
    
    onValuesChange(valuesArray);
  };

  const handleAddField = (fieldId) => {
    if (!fieldId) return;
    const field = availableFields.find(f => f.id === fieldId);
    if (!field) return;
    
    // Initialize with appropriate default value based on type
    let defaultValue = null;
    if (field.field_type === 'checkbox') defaultValue = false;
    else if (field.field_type === 'number') defaultValue = 0;
    else if (field.field_type === 'select' || field.field_type === 'dropdown') {
      // For dropdowns, initialize as empty string but still add the field
      defaultValue = '';
    } else {
      defaultValue = '';
    }
    
    // Add the field to localValues to show it in the UI
    const newValues = { ...localValues, [fieldId]: defaultValue };
    setLocalValues(newValues);
    
    // Notify parent
    const valuesArray = Object.entries(newValues)
      .map(([field_id, value]) => ({ field_id, value }));
    onValuesChange(valuesArray);
    
    setSelectedFieldId('');
    setOpenFieldSelector(false);
  };

  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [fieldId]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleValueChange(fieldId, {
        filename: file.name,
        url: response.data.url,
        size: file.size,
        type: file.type
      });
      
      toast.success(t('customFields.fileUploaded') || 'File uploaded successfully');
    } catch (error) {
      toast.error(t('customFields.uploadError') || 'Error uploading file');
    }
    
    setUploading(prev => ({ ...prev, [fieldId]: false }));
  };

  // Get fields that are already added
  const addedFieldIds = Object.keys(localValues);
  
  // Get fields available to add (not already added)
  const fieldsToAdd = availableFields.filter(f => !addedFieldIds.includes(f.id));
  
  // Filter by search
  const filteredFieldsToAdd = fieldsToAdd.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (f.group_name && f.group_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Get added fields with their definitions
  const addedFields = addedFieldIds
    .map(id => availableFields.find(f => f.id === id))
    .filter(Boolean);

  const renderFieldInput = (field) => {
    const value = localValues[field.id];
    
    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={field.placeholder || t('customFields.enterValue') || 'Enter value...'}
            className="flex-1"
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={field.placeholder || t('customFields.enterValue') || 'Enter value...'}
            rows={2}
            className="flex-1"
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value ? Number(e.target.value) : '')}
            placeholder={field.placeholder || '0'}
            className="flex-1 max-w-[150px]"
          />
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!value}
              onCheckedChange={(checked) => handleValueChange(field.id, checked)}
            />
            <span className="text-sm text-muted-foreground">
              {value ? (t('yes') || 'Yes') : (t('no') || 'No')}
            </span>
          </div>
        );
      
      case 'select':
      case 'dropdown':
        const options = field.options || [];
        return (
          <Select
            value={value || ''}
            onValueChange={(val) => handleValueChange(field.id, val)}
          >
            <SelectTrigger className="flex-1 max-w-[200px]">
              <SelectValue placeholder={t('customFields.selectOption') || 'Select option...'} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt, idx) => (
                <SelectItem key={idx} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'date':
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "flex-1 max-w-[200px] justify-start text-left font-normal",
                  !value && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(new Date(value), 'PPP') : (t('customFields.pickDate') || 'Pick a date')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={value ? new Date(value) : undefined}
                onSelect={(date) => handleValueChange(field.id, date?.toISOString())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );
      
      case 'file':
        return (
          <div className="flex items-center gap-2 flex-1">
            {value?.filename ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
                <FileText className="h-4 w-4" />
                <span className="text-sm truncate max-w-[150px]">{value.filename}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => handleValueChange(field.id, null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  ref={el => fileInputRefs.current[field.id] = el}
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleFileUpload(field.id, e.target.files[0]);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRefs.current[field.id]?.click()}
                  disabled={uploading[field.id]}
                >
                  {uploading[field.id] ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {t('customFields.uploadFile') || 'Upload'}
                </Button>
              </>
            )}
          </div>
        );
      
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={t('customFields.enterValue') || 'Enter value...'}
            className="flex-1"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add new field selector */}
      <div className="flex items-center gap-2">
        <Popover open={openFieldSelector} onOpenChange={setOpenFieldSelector}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={openFieldSelector}
              className="flex-1 justify-between"
              disabled={fieldsToAdd.length === 0}
            >
              <span className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                {t('customFields.addField') || 'Add Custom Field'}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput 
                placeholder={t('customFields.searchFields') || 'Search fields...'} 
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList className="max-h-[200px] overflow-y-auto">
                <CommandEmpty>{t('customFields.noFieldsFound') || 'No fields found'}</CommandEmpty>
                <CommandGroup>
                  {filteredFieldsToAdd.map((field) => (
                    <CommandItem
                      key={field.id}
                      value={field.name}
                      onSelect={() => handleAddField(field.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{field.name}</span>
                        {field.group_name && (
                          <span className="text-xs text-muted-foreground">{field.group_name}</span>
                        )}
                      </div>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {field.field_type}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* List of added fields */}
      {addedFields.length > 0 ? (
        <div className="space-y-3">
          {addedFields.map((field) => (
            <div 
              key={field.id} 
              className="flex items-start gap-3 p-3 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="font-medium text-sm">
                    {field.name}
                    {field.is_required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {field.group_name && (
                    <Badge variant="secondary" className="text-xs">
                      {field.group_name}
                    </Badge>
                  )}
                </div>
                {field.description && (
                  <p className="text-xs text-muted-foreground">{field.description}</p>
                )}
                <div className="flex items-center gap-2">
                  {renderFieldInput(field)}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => handleRemoveField(field.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground border rounded-lg border-dashed">
          <Plus className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('customFields.noFieldsAdded') || 'No custom fields added yet'}</p>
          <p className="text-xs mt-1">{t('customFields.useButtonAbove') || 'Use the button above to add fields'}</p>
        </div>
      )}

      {/* Summary */}
      {addedFields.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
          <span>
            {addedFields.length} {addedFields.length === 1 
              ? (t('customFields.fieldAdded') || 'field added') 
              : (t('customFields.fieldsAdded') || 'fields added')}
          </span>
          <span className="text-green-600 flex items-center gap-1">
            <Check className="h-3 w-3" />
            {t('customFields.autoSaved') || 'Auto-saved'}
          </span>
        </div>
      )}
    </div>
  );
};

export default CustomFieldInlineEditor;
